/**
 * Join public API client (WU-19) — the four no-session join endpoints, every
 * response parsed through the SHARED zod schemas. Mirrors api.ts (same-origin
 * `/api/v1`, same ApiError) without modifying it: its request helper is
 * module-private, so a minimal copy lives here (as change-order-api.ts does).
 *
 * The web never mints a session: claims omit `signIn` (server default false)
 * and end on "Open the app".
 */
import {
  AuthConfigResponseSchema,
  BRAND,
  GoogleCheckResponseSchema,
  JoinClaimResponseSchema,
  JoinPreviewSchema,
} from '@buildledger/shared';
import type { GoogleCheckResponse } from '@buildledger/shared';
import type {
  JoinClaimRequest,
  JoinClaimResponse,
  JoinCodeClaimRequest,
  JoinPreview,
} from '@buildledger/shared';

import { ApiError } from './api';

async function request(path: string, init?: RequestInit): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError(0, 'Could not reach the server — check your connection and try again.');
  }
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? Array.isArray((body as { message: unknown }).message)
          ? ((body as { message: string[] }).message[0] ?? res.statusText)
          : String((body as { message: unknown }).message)
        : res.statusText;
    throw new ApiError(res.status, message);
  }
  return body;
}

/** What the web form sends — never `signIn` / `client`. */
export type JoinClaimBody = Pick<JoinClaimRequest, 'name' | 'email' | 'password'>;
export type JoinCodeClaimBody = Pick<JoinCodeClaimRequest, 'code' | 'name' | 'email' | 'password'>;

// -------------------------------------------------------------------- link

export function getJoinPreview(token: string): Promise<JoinPreview> {
  return request(`/public/join/${encodeURIComponent(token)}`).then((body) =>
    JoinPreviewSchema.parse(body),
  );
}

export function postJoinClaim(token: string, dto: JoinClaimBody): Promise<JoinClaimResponse> {
  return request(`/public/join/${encodeURIComponent(token)}/claim`, {
    method: 'POST',
    body: JSON.stringify({ name: dto.name, email: dto.email, password: dto.password }),
  }).then((body) => JoinClaimResponseSchema.parse(body));
}

// -------------------------------------------------------------------- code

export function postJoinCodePreview(code: string): Promise<JoinPreview> {
  return request('/public/join/code', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }).then((body) => JoinPreviewSchema.parse(body));
}

export function postJoinCodeClaim(dto: JoinCodeClaimBody): Promise<JoinClaimResponse> {
  return request('/public/join/code/claim', {
    method: 'POST',
    body: JSON.stringify({
      code: dto.code,
      name: dto.name,
      email: dto.email,
      password: dto.password,
    }),
  }).then((body) => JoinClaimResponseSchema.parse(body));
}

// ------------------------------------------------------------------ google

/**
 * Whether new companies may be created right now (SIGNUP_MODE on the server).
 * true while unknown, so a network hiccup never hides a form the server would
 * accept — the server refuses a closed sign-up on its own either way.
 */
export function getSignupsOpen(): Promise<boolean> {
  return request('/auth/config')
    .then((body) => AuthConfigResponseSchema.parse(body).signups.open)
    .catch(() => true);
}

/** The Google web client id, or null when Google sign-in is not configured. */
export function getGoogleWebClientId(): Promise<string | null> {
  return request('/auth/config')
    .then((body) => AuthConfigResponseSchema.parse(body).google.webClientId)
    .catch(() => null);
}

/**
 * Who is this Google identity to us? Never signs the browser in: a new person
 * gets a sign-up token for the claim, an existing one is told to use the app.
 */
export function postGoogleCheck(idToken: string): Promise<GoogleCheckResponse> {
  return request('/auth/google/check', {
    method: 'POST',
    body: JSON.stringify({ idToken }),
  }).then((body) => GoogleCheckResponseSchema.parse(body));
}

/** Join through a link with a Google identity. No `signIn`: the web never mints a session. */
export function postJoinClaimWithGoogle(
  token: string,
  signupToken: string,
  name?: string,
): Promise<JoinClaimResponse> {
  return request(`/public/join/${encodeURIComponent(token)}/claim/google`, {
    method: 'POST',
    body: JSON.stringify({ signupToken, ...(name ? { name } : {}) }),
  }).then((body) => JoinClaimResponseSchema.parse(body));
}

export function postJoinCodeClaimWithGoogle(
  code: string,
  signupToken: string,
  name?: string,
): Promise<JoinClaimResponse> {
  return request('/public/join/code/claim/google', {
    method: 'POST',
    body: JSON.stringify({ code, signupToken, ...(name ? { name } : {}) }),
  }).then((body) => JoinClaimResponseSchema.parse(body));
}

// ------------------------------------------------------------------ errors

/**
 * Show the "no longer valid" screen: 410 always; 404 only for a link (a code
 * that is not found is a typo — the code page keeps the boxes and says so).
 */
export function isUnusableError(err: unknown, via: 'link' | 'code'): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.status === 410 || (via === 'link' && err.status === 404);
}

/** Human copy for a failed preview or claim (410 is handled by the caller). */
export function joinErrorMessage(err: unknown, via: 'link' | 'code'): string {
  if (!(err instanceof ApiError)) return 'Something went wrong — try again.';
  if (err.status === 429) return 'Too many tries — wait a minute, then try again.';
  if (err.status === 409) {
    if (/another company/i.test(err.message)) return err.message;
    const lead = 'This email already has an account. Sign in and enter the code instead:';
    const steps = `open the ${BRAND.name} app, go to Settings → Join with a code.`;
    return via === 'link'
      ? `${lead} ${steps} Ask whoever sent this link for the 8-digit code.`
      : `${lead} ${steps}`;
  }
  if (err.status === 404 && via === 'code') {
    return 'That code was not found — check the digits and try again.';
  }
  return err.message || 'Something went wrong — try again.';
}
